import { TreeViewBaseItem } from "@mui/x-tree-view-pro";
import { RatelistType } from "@sinch/core/entities/serverEnums.types";
import { includes } from "ramda";
import React, { useCallback, useMemo } from "react";
import { FieldValues } from "react-hook-form";
import { RateBuilderRoot } from "./BuilderTree/RateBuilderRoot";
import { RateBuilderItem } from "./RateTreeItem/RateBuilderItem";
import { RateBuilderTree } from "@sinch/components/application/RatelistBuilder/BuilderTree/RateBuilderTree";
import {
  RateBuilderTreeViewItem,
  TreeMethods,
  UseTreeViewExpansionParameters,
} from "@sinch/components/application/RatelistBuilder/types";
import { RateBuilderSettingsProvider } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { getParentNodeById } from "@sinch/components/application/RatelistBuilder/utils/treeUtils";

const treeSlots = {
  item: RateBuilderItem,
  root: RateBuilderRoot,
};
type OnExpandedItemsChangeMethod = NonNullable<UseTreeViewExpansionParameters["onExpandedItemsChange"]>;
export const RatelistBuilder = <TreeItem extends TreeViewBaseItem, TData extends FieldValues = FieldValues>({
  rateBuilder,
  rateType,
  mode = "edit",
  disableConditions,
  disableNesting,
}: {
  rateBuilder: TreeMethods<TreeItem, TData>;
  mode?: "edit" | "view" | 'override';
  rateType: RatelistType;
  disableConditions?: boolean;
  disableNesting?: boolean;
}) => {
  const { handleExpandedItemsChange } = useRateBuilderTreeLogic(rateBuilder);

  const items = useMemo(() => structuredClone(rateBuilder.tree), [rateBuilder.tree]);

  return (
    <RateBuilderSettingsProvider
      disableConditions={disableConditions}
      disableNesting={disableNesting}
      mode={mode}
      rateType={rateType}
    >
      <RateBuilderTree<RateBuilderTreeViewItem>
        disableSelection
        draggable={false}
        expandedItems={rateBuilder.expanded}
        expansionTrigger="iconContainer"
        itemChildrenIndentation={0}
        items={items}
        onExpandedItemsChange={handleExpandedItemsChange}
        slots={treeSlots}
      />
    </RateBuilderSettingsProvider>
  );
};
const useRateBuilderTreeLogic = <TreeItem extends RateBuilderTreeViewItem, TData extends FieldValues = FieldValues>(
  rateBuilder: TreeMethods<TreeItem, TData>
) => {
  const handleExpandedItemsChange = useCallback<OnExpandedItemsChangeMethod>(
    (_: React.SyntheticEvent | null, itemIds: string[]) => {
      const withoutFoldedDeep = itemIds.filter((nodeId) => {
        let parent = getParentNodeById(rateBuilder.tree, nodeId);
        while (parent && parent.type === "wrapper") {
          parent = getParentNodeById(rateBuilder.tree, parent.id);
        }
        return !parent || includes(parent.id, itemIds);
      });

      rateBuilder.expand(withoutFoldedDeep, true);
    },
    [rateBuilder]
  );

  return {
    handleExpandedItemsChange,
  };
};
